import React, { useEffect, useState, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Pencil, Copy, Trash2 } from 'lucide-react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import Toggle from '../UI/Toggle';
import { AgTableCard } from '../UI';
import { getAgGridSortStorageKey } from '../UI/AgGrid';
import DeleteTemplateWithDependencyModal from './DeleteTemplateWithDependencyModal';
import { ExperienceContext } from '../../contexts/ExperienceContext';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { permission } from '../../config/permission';
import { saveTemplates } from '../../utils/APIs';

const ManageTemplates = () => {
    const navigate = useNavigate();
    const { rawData, setRawData, getTemplatesRawData, loading, setLoading, selectedTemplateId, setSelectedTemplateId } = useContext(ExperienceContext);
    const { enums, content } = useContentfulContext();
    const { userData, checkPermission } = useUserContext();

    const [selectedAction, setSelectedAction] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [activeFilters, setActiveFilters] = useState(['local', 'global']);
    const [rowData, setRowData] = useState([]);
    const [gridApi, setGridApi] = useState(null);

    useEffect(() => {
        getTemplatesRawData(true);
    }, []);

    useEffect(() => {
        if (selectedTemplateId && selectedTemplateId.length > 0) {
            const found = rawData.find((item) => String(item._id) === String(selectedTemplateId));
            if (found) {
                setSelectedTemplate(found);
            }
        }
    }, [selectedTemplateId, rawData]);

    useEffect(() => {
        const filteredData = rawData.filter((item) => activeFilters.includes(item.scope));
        setRowData(filteredData);
    }, [rawData, activeFilters]);

    const extractUserName = (val) => {
        if (!val) return "-";
        if (typeof val === 'string') return val;
        if (typeof val === 'object') return val.name || val.email || val.username || "-";
        return String(val);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "-";
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return "-";
        const optionsDate = {
            month: 'short',
            day: '2-digit',
            year: 'numeric'
        };
        const formattedDate = date.toLocaleDateString('en-US', optionsDate);
        const hours12 = date.getHours() % 12 || 12;
        const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
        const formattedTime = hours12 + ':' + ('0' + date.getMinutes()).slice(-2) + ' ' + ampm;
        return `${formattedDate}, ${formattedTime}`;
    };

    const colDefs = useMemo(() => [
        {
            headerName: content.MANAGE_TEMPLATE_CONTENT.table_headers.template_name,
            field: "name",
            filter: true,
            cellRenderer: ({ data }) => {
                if (!data) return null;
                const hasAllRequiredFields = 'html' in data && 'css' in data && 'json_schema' in data;
                return (
                    <div className="flex items-center gap-2 min-w-0">
                        <button
                            type="button"
                            className={`min-w-0 truncate text-left font-medium text-xs md:text-sm bg-transparent border-0 p-0 ${
                                !hasAllRequiredFields
                                    ? 'text-stride-muted-light cursor-not-allowed'
                                    : 'text-primary hover:underline cursor-pointer'
                            }`}
                            disabled={!hasAllRequiredFields}
                            title={!hasAllRequiredFields ? content.MANAGE_TEMPLATE_CONTENT.not_accessable : data.name}
                            onClick={() => {
                                if (hasAllRequiredFields) {
                                    navigate(`/experience/edit-template/${data._id}`);
                                }
                            }}
                        >
                            {data.name}
                        </button>
                    </div>
                );
            },
            suppressMovable: true
        },
        {
            headerName: content.MANAGE_TEMPLATE_CONTENT.table_headers.type,
            field: "type",
            cellRenderer: ({ data }) => {
                const typeName = enums?.templateTypes?.[data?.type]?.name || data?.type || "-";
                return (
                    <span className="stride-badge-pill bg-surface-alt text-stride-body border border-border">
                        {typeName}
                    </span>
                );
            },
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: content.MANAGE_TEMPLATE_CONTENT.table_headers.scope,
            field: "scope",
            cellRenderer: ({ data }) => {
                const scope = data?.scope;
                const scopeName = enums?.templateScopes?.[scope]?.name || scope || "-";
                const isGlobal = scope === 'global';
                return (
                    <span className={`stride-badge-pill ${
                        isGlobal
                            ? 'bg-indigo-50 text-indigo-700 border border-indigo-200/60'
                            : 'bg-primary-soft text-primary border border-primary/20'
                    }`}>
                        {scopeName}
                    </span>
                );
            },
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: content.MANAGE_TEMPLATE_CONTENT.table_headers.platform,
            field: "platform",
            cellRenderer: ({ data }) => {
                const platformName = enums?.templatePlatforms?.[data?.platform]?.name || data?.platform || "-";
                return (
                    <span className="text-xs text-stride-body font-medium">
                        {platformName}
                    </span>
                );
            },
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: content.MANAGE_TEMPLATE_CONTENT.table_headers.managed_by,
            field: "managed_by",
            cellRenderer: ({ data }) => {
                return (
                    <span className="text-xs text-stride-body font-medium truncate block">
                        {extractUserName(data?.managed_by)}
                    </span>
                );
            },
            sortable: false,
            resizable: false,
            suppressMovable: true
        },
        {
            headerName: content.MANAGE_TEMPLATE_CONTENT.table_headers.last_edited,
            field: "updatedAt",
            cellRenderer: ({ data }) => {
                return (
                    <span className="text-xs text-stride-muted tabular-nums">
                        {formatDate(data?.updatedAt)}
                    </span>
                );
            },
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            suppressMovable: true,
            sortable: true
        },
        {
            headerName: 'Actions',
            field: 'actions',
            width: 120,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: ({ data }) => {
                if (!data) return null;
                const hasAllRequiredFields = 'html' in data && 'css' in data && 'json_schema' in data;
                const isLocal = data?.scope === enums?.templateScopes?.local?.id;
                return (
                    <div className="stride-table-actions">
                        {isLocal && (
                            <button
                                type="button"
                                className={`stride-table-btn stride-table-btn-edit ${!hasAllRequiredFields ? 'opacity-40 cursor-not-allowed' : ''}`}
                                title={content.MANAGE_TEMPLATE_CONTENT.more_info.edit}
                                disabled={!hasAllRequiredFields}
                                onClick={() => {
                                    if (hasAllRequiredFields) {
                                        navigate(`/experience/edit-template/${data._id}`);
                                    }
                                }}
                            >
                                <Pencil size={14} />
                            </button>
                        )}
                        {checkPermission(permission.TEMPLATE_CREATE) && (
                            <button
                                type="button"
                                className="stride-table-btn stride-table-btn-copy"
                                title={content.MANAGE_TEMPLATE_CONTENT.more_info.duplicate}
                                onClick={() => {
                                    setSelectedTemplate(data);
                                    duplicateTemplateHandler(data);
                                }}
                            >
                                <Copy size={14} />
                            </button>
                        )}
                        {isLocal && checkPermission(permission.TEMPLATE_DELETE) && (
                            <button
                                type="button"
                                className="stride-table-btn stride-table-btn-delete"
                                title={content.MANAGE_TEMPLATE_CONTENT.more_info.delete}
                                onClick={() => {
                                    setSelectedTemplateId(data._id);
                                    setSelectedTemplate(data);
                                    setSelectedAction('delete');
                                }}
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                );
            },
        },
    ], [content, enums, navigate, setSelectedTemplateId, checkPermission]);

    const onFilterTextBoxChanged = (event) => {
        const filterValue = (event?.target?.value || '').toLowerCase();
        if (gridApi) {
            gridApi.setFilterModel(filterValue ? {
                name: { type: 'contains', filter: filterValue }
            } : null);
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("manage-templates-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [
                    { colId: 'updatedAt', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 400,
            columnLimits: [
                { colId: "name", minWidth: 280 },
                { colId: "updatedAt", minWidth: 160 },
                { colId: "managed_by", minWidth: 140 },
                { colId: "actions", minWidth: 120 },
                { colId: "type", minWidth: 140 },
                { colId: "scope", minWidth: 100 },
                { colId: "platform", minWidth: 120 }
            ],
        };
    }, []);

    const isNameUnique = (name) => {
        return !rawData.some(item => item.name === name);
    };

    const duplicateName = (templateName) => {
        let newName = `${templateName} Copy`;
        let counter = 1;
        while (!isNameUnique(newName)) {
            newName = `${templateName} Copy ${counter++}`;
        }
        return newName;
    };

    const duplicateTemplateHandler = async (targetTemplate = selectedTemplate) => {
        const tpl = targetTemplate || selectedTemplate;
        if (!tpl) return;
        try {
            setLoading(true);
            const payload = {
                name: duplicateName(tpl.name),
                html: tpl.html,
                css: tpl.css,
                type: tpl.type,
                config_json: tpl.config_json,
                json_schema: tpl.json_schema
            };
            const result = await saveTemplates(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                payload
            );
            const newRawData = [...rawData, result.data];
            setRawData(newRawData);
            setSelectedTemplateId(result.data._id);
            navigate('/experience/create-template');
        } catch (error) {
            console.error('Error duplicating template:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (filter) => {
        setActiveFilters((prevFilters) =>
            prevFilters.includes(filter)
                ? prevFilters.filter((f) => f !== filter)
                : [...prevFilters, filter]
        );
    };

    const handleNavigateToPreviews = () => {
        navigate('/experience/templates-preview', {
            state: {
                templates: rowData
            }
        });
    };

    return (
        <main id="main" className="stride-page-shell bg-gradient-to-b from-surface via-white to-white min-h-screen space-y-6">
            {/* Header & Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <p className="stride-eyebrow">Template Management</p>
                    <div className="flex items-baseline gap-3">
                        <h1 className="text-2xl md:text-3xl stride-heading m-0">
                            {content.MANAGE_TEMPLATE_CONTENT.title}
                        </h1>
                        <span className="text-xs md:text-sm text-stride-muted font-normal">
                            {rowData.length} {rowData.length === 1 ? 'template' : 'templates'}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
                    {checkPermission(permission.TEMPLATE_OP_BULK_PREVIEW) && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleNavigateToPreviews}
                        >
                            {content.MANAGE_TEMPLATE_CONTENT.cta_text.bulk_preview}
                        </Button>
                    )}
                    {checkPermission(permission.TEMPLATE_CREATE) && (
                        <Button
                            id="create-template-btn-id"
                            variant="primary"
                            size="sm"
                            onClick={() => {
                                navigate('/experience/create-template');
                                setSelectedTemplateId('');
                            }}
                            icon={<Plus size={15} />}
                        >
                            {content.MANAGE_TEMPLATE_CONTENT.cta_text.create_template}
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter and Table Container */}
            <AgTableCard
                className="shadow-xs flex-1 flex flex-col min-h-[580px]"
                header={
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="w-full sm:w-80">
                            <Input
                                prefixIcon={<Search size={15} />}
                                placeholder={content.MANAGE_TEMPLATE_CONTENT.search_placeholder}
                                onChange={onFilterTextBoxChanged}
                                clearable
                            />
                        </div>
                        {/* Scope Filter Toggles */}
                        <div className="flex items-center gap-4 px-3 py-1.5 bg-white rounded-lg border border-border-table shadow-xs">
                            {['local', 'global'].map((type) => {
                                const isActive = activeFilters.includes(type);
                                const label = content?.CHOOSE_TEMPLATE_CONTENT?.[type] || (type === 'local' ? 'Local' : 'Global');
                                return (
                                    <Toggle
                                        key={type}
                                        id={`manage-template-scope-${type}`}
                                        checked={isActive}
                                        onChange={() => handleFilterChange(type)}
                                        label={label}
                                        size="sm"
                                    />
                                );
                            })}
                        </div>
                    </div>
                }
                tableId="manage-templates-table"
                theme="ag-theme-quartz"
                loading={loading}
                rowData={rowData}
                columnDefs={colDefs}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={onGridReady}
                pagination={true}
                paginationPageSize={content.MANAGE_TEMPLATE_CONTENT.default_page_size || 20}
                paginationPageSizeSelector={[10, 20, 50, 100]}
                getRowClass={(params) => {
                    const { data } = params;
                    const hasAllRequiredFields = data && 'html' in data && 'css' in data && 'json_schema' in data;
                    return !hasAllRequiredFields ? 'ag-row-disabled' : '';
                }}
            />

            {selectedAction === 'delete' && selectedTemplateId && (
                <DeleteTemplateWithDependencyModal
                    setSelectedAction={setSelectedAction}
                    selectedTemplateId={selectedTemplateId}
                    setSelectedTemplate={setSelectedTemplate}
                />
            )}
        </main>
    );
};

export default ManageTemplates;
